import { ExportTypeEnum, PagedResult } from "@king-ict/eupisi-ui/models";
import { api, axiosBaseQuery } from "@king-ict/eupisi-ui/utils";
import { createApi } from "@reduxjs/toolkit/query/react";

import {
  SaveSchoolCandidateInstitutionAssignment,
  SchoolCandidateBatchStatus,
  SchoolCandidateResidenceChange,
  SchoolCandidateResidenceChangeSearch,
  SchoolCandidateSearch,
} from "../models";
import { SaveResult } from "../models/Shared";

const schoolCandidateUrlPrefix: string = "school-candidates";

export type SchoolCandidateNewResidencesRequest = {
  countyId: number;
  batchId: number;
  search: SchoolCandidateResidenceChangeSearch;
};

type SchoolCandidateListExportRequest = {
  search: SchoolCandidateSearch;
  type: ExportTypeEnum;
};

export type SchoolCandidateResidencesDownloadRequest = {
  countyId: number;
  batchId: number;
  schoolCandidateIds: number[];
};

export type SchoolCandidateResidencesDownloadResult = {
  schoolCandidateIds: number[];
};

export type SchoolCandidateBatchDto = {
  id: number;
  processedAt: string;
  schoolYearId: number;
};

export const schoolCandidateApi = createApi({
  reducerPath: "schoolCandidateApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["SchoolCandidateAddresses"],
  endpoints: (build) => ({
    getSchoolCandidateNewResidences: build.query<
      PagedResult<SchoolCandidateResidenceChange>,
      SchoolCandidateNewResidencesRequest
    >({
      query: ({ countyId, batchId, search }) => ({
        url: `${schoolCandidateUrlPrefix}/get-new-residences/${countyId}/${batchId}`,
        method: "post",
        data: search,
      }),
      providesTags: ["SchoolCandidateAddresses"],
    }),
    startSchoolCandidateBatch: build.mutation<SaveResult<number>, number>({
      query: (countyId) => ({
        url: `${schoolCandidateUrlPrefix}/start-batch/${countyId}`,
        method: "post",
      }),
    }),
    saveInstitutionAssignment: build.mutation<
      SaveResult<SaveSchoolCandidateInstitutionAssignment>,
      SaveSchoolCandidateInstitutionAssignment
    >({
      query: (data) => ({
        url: `${schoolCandidateUrlPrefix}/save-assignment`,
        method: "post",
        data,
      }),
      invalidatesTags: ["SchoolCandidateAddresses"],
    }),
    setInstitutionAssignment: build.mutation<
      SaveResult<SaveSchoolCandidateInstitutionAssignment>,
      SaveSchoolCandidateInstitutionAssignment
    >({
      query: (data) => ({
        url: `${schoolCandidateUrlPrefix}/set-assignment`,
        method: "post",
        data,
      }),
      invalidatesTags: ["SchoolCandidateAddresses"],
    }),
    getSchoolCandidateLastBatchStatus: build.query<
      SchoolCandidateBatchStatus[],
      number[]
    >({
      query: (countyIds) => ({
        url: `${schoolCandidateUrlPrefix}/last-batch-status`,
        method: "post",
        data: { countyIds },
      }),
    }),
    getSchoolCandidateBatchHistory: build.query<
      SchoolCandidateBatchDto[],
      { countyId: number; schoolYearId: number }
    >({
      query: ({ countyId, schoolYearId }) => ({
        url: `${schoolCandidateUrlPrefix}/batch-history/${countyId}/${schoolYearId}`,
        method: "get",
      }),
    }),
    getSchoolCandidateListExport: build.mutation<
      unknown,
      SchoolCandidateListExportRequest
    >({
      queryFn: async ({ search, type }) => {
        try {
          const data = await api.download(
            `${schoolCandidateUrlPrefix}/export/${type}`,
            "post",
            search
          );

          return { data };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error,
            },
          };
        }
      },
    }),
    downloadSchoolCandidateResidences: build.mutation<
      SaveResult<SchoolCandidateResidencesDownloadResult>,
      SchoolCandidateResidencesDownloadRequest
    >({
      query: ({ countyId, batchId, schoolCandidateIds }) => ({
        url: `${schoolCandidateUrlPrefix}/residences/${countyId}/${batchId}`,
        method: "put",
        data: { schoolCandidateIds },
      }),
      invalidatesTags: ["SchoolCandidateAddresses"],
    }),
  }),
});

export const {
  useLazyGetSchoolCandidateNewResidencesQuery,
  useStartSchoolCandidateBatchMutation,
  useSaveInstitutionAssignmentMutation,
  useSetInstitutionAssignmentMutation,
  useGetSchoolCandidateLastBatchStatusQuery,
  useLazyGetSchoolCandidateBatchHistoryQuery,
  useGetSchoolCandidateListExportMutation,
  useDownloadSchoolCandidateResidencesMutation,
} = schoolCandidateApi;
